// Real API Order Types
export interface BespokeDetails {
  garmentType: string
  fabricChoice: string
  sizeAndFitPreference: string
  color: string
  sellingPrice: number
  costPrice: number
  quantity: number
  estimatedDeliveryDate: string
  deliveryType: 'DELIVERY' | 'PICKUP'
  deliveryFee: number
  imageUrls?: string[]
}

/**
 * An order line as the API actually returns it — verified live on development 2026-09-03.
 *
 * ⚠️ It is FLAT. The deeply-nested `productVariant.productColour.productMaterial.productSize.product`
 * shape this type used to declare does not exist in the payload, so the transform below dereferenced
 * `undefined` and threw for every order that had items — taking the whole Orders page down with it.
 * The nested shape is kept OPTIONAL in case an older environment still serves it, and the transform
 * reads flat-first and falls back.
 *
 * There is deliberately NO item-type discriminator here: a line carries `variantId` and nothing that
 * says product vs package vs booking. That is why PE-2167's mixed-item-type acceptance criteria are
 * not buildable yet — see the note on `ApiOrder`.
 */
export interface ApiOrderItem {
  id: string
  orderId?: string
  /** The product variant purchased. The only product reference on a line. */
  variantId?: string
  quantity: number
  /** Price per unit, already discounted. `price` is the legacy name. */
  unitPrice?: number
  price?: number
  /** unitPrice * quantity, server-computed. */
  subtotal?: number
  /** Pre-discount unit price, when a discount applied. */
  originalPrice?: number
  discountPercentage?: number
  /** Free-form variant attributes (size/colour/material), when the server resolves them. */
  attributes?: Record<string, unknown> | null
  tenantId?: string | null
  /** Display name for the line. Often null on older orders — fall back to the variant id. */
  itemName?: string | null
  customProductionTimeInDays?: number | null
  taxClass?: string | null
  /** Legacy nested shape. Absent on development; read only as a fallback. */
  productVariant?: {
    id: string
    productColour: {
      id: string
      quantity: number
      weight: number
      imageUrls: string[]
      colour: { id: string; name: string; hexValue: string }
      productMaterial: {
        id: string
        material: { id: string; name: string }
        productSize: {
          id: string
          size: { id: string; name: string }
          product: { id: string; name: string; basePrice: number }
        }
      }
    }
  }
}

/** One entry in an order's status timeline (`ApiOrder.history`). */
export interface ApiOrderHistoryEntry {
  id: string
  orderId: string
  status: ApiOrderStatus
  note: string | null
  timestamp: string
}

export type ApiOrderStatus =
  | "PENDING"
  | "ORDER_CONFIRMED"
  | "ORDER_RECEIVED"
  | "TRANSFER_FAILED"
  | "REFUNDED"
  | "PROCESSING"
  | "EN_ROUTE"
  | "DELIVERED"
  | "CANCELLED"
  | "RETURNED"

/**
 * An order as the API actually returns it — verified live on development 2026-09-03.
 *
 * ⚠️ The tenant key is `tenantId`, not `merchantId`, and there is no nested `customer` object:
 * the buyer arrives as `customerEmail` / `guestEmail` plus names inside `fulfillmentMetadata`.
 * Reading a `customer` that is never sent is what made every row render "Unknown Customer".
 *
 * ⚠️ `orderType` is `PREMADE | BESPOKE` (rack-api `OrderType`) — it is order-WIDE, and there is no
 * per-line type discriminator anywhere in the payload, so an order cannot currently be rendered as
 * a mix of product / package / booking lines (PE-2167).
 */
export interface ApiOrder {
  id: string;
  customerId?: string | null;
  customerEmail?: string | null;
  guestEmail?: string | null;
  adminId?: string | null;
  /** The merchant that owns the order. `merchantId` is the legacy name; the API sends `tenantId`. */
  tenantId?: string;
  merchantId?: string;
  solutionId?: string | null;
  total: number;
  totalWeight: number;
  totalCostPrice: number;
  trackingId: string;
  friendlyOrderId?: string;
  paymentReference: string | null;
  /** rack-api OrderType. 'STANDARD' was never a real value — the enum is PREMADE | BESPOKE. */
  orderType?: 'PREMADE' | 'BESPOKE';
  contactPhone?: string | null;
  shippingMethod?: string;
  shippingCost?: number;
  /** DHL-generated tracking number, set when shipment is booked via DHL */
  dhlTrackingNumber?: string;
  /** Carrier name for manual (non-DHL) shipments */
  carrier?: string;
  /** Tracking number for manual (non-DHL) shipments */
  manualTrackingNumber?: string;
  /** Courier-agnostic fulfilment state, separate from the order `status`. */
  fulfillmentStatus?: string | null;
  fulfillmentProvider?: string | null;
  fulfillmentReference?: string | null;
  /** Free-form provider blob. Carries the buyer's first/last name and phone on guest checkouts. */
  fulfillmentMetadata?: {
    firstName?: string | null;
    lastName?: string | null;
    contactPhone?: string | null;
    shippingMethod?: string | null;
    isGuest?: boolean;
    [k: string]: unknown;
  } | null;
  deliveryMethod?: string | null;
  pickupAgent?: string | null;
  selfArrangedInstruction?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
  /** Courier tracking number. Supersedes the `dhlTrackingNumber` this type used to expect. */
  trackingCode?: string | null;
  trackingUrl?: string | null;
  labelUrl?: string | null;
  /** Status timeline, newest last. Only returned on the by-id read, not the list. */
  history?: ApiOrderHistoryEntry[];
  bespokeDetails?: BespokeDetails | null;
  status: // | "PAID"
  | "PENDING"
    | "ORDER_CONFIRMED"
    | "ORDER_RECEIVED"
    | "TRANSFER_FAILED"
    | "REFUNDED"
    | "PROCESSING"
    | "EN_ROUTE"
    | "DELIVERED"
    | "CANCELLED"
    | "RETURNED";
  additionalInfo: string;
  createdAt: string;
  updatedAt: string;
  deliveryDate: string | null;
  /** Never sent by the API today — kept optional for environments that still nest the buyer. */
  customer?: {
    id: string;
    fullName: string;
    email: string;
  };
  items: ApiOrderItem[];
  /** Null on guest/pickup orders. */
  address?: {
    id: string;
    streetLines: string[];
    city: string;
    stateOrProvinceCode: string;
    postalCode: string;
    countryCode: string;
    placeId: string | null;
    formattedAddress: string;
    createdAt: string;
    updatedAt: string;
    phone?: string;
  };
}

export interface ApiOrdersResponse {
  data: ApiOrder[]
  meta: {
    hasNextPage: boolean
    hasPreviousPage: boolean
    startCursor: string
    endCursor: string
    totalCount: number
    lastCursor: string
  }
  links: {
    first: string
    last: string
  }
  execution_time_ms: number
}

// Frontend Order Types (for display compatibility)
export interface OrderItem {
  id: string
  name: string
  costPrice: number
  quantity: number
  unitPrice: number
  total: number
  /** Empty string when the API doesn't resolve variant detail — render as "—", not "undefined". */
  size: string
  color: string
  material: string
  /** The only product reference a live line carries; useful as a fallback label. */
  variantId?: string
}

export interface Order {
  id: string;
  merchantId: string;
  orderId: string;
  friendlyOrderId?: string;
  contactPhone?: string; // Optional field for backward compatibility
  address?: {
    phone?: string; // Optional field for backward compatibility
  }
  /** DHL-generated tracking number */
  dhlTrackingNumber?: string;
  /** Carrier name for manual (non-DHL) shipments */
  carrier?: string;
  /** Tracking number for manual (non-DHL) shipments */
  manualTrackingNumber?: string;
  /** Shipping method label (e.g. "delivery", "pickup") */
  shippingMethod?: string;
  date: string;
  orderType?: 'PREMADE' | 'BESPOKE';
  bespokeDetails?: BespokeDetails | null;
  /** Everything the merchant needs to answer "where is it?" — all optional, all commonly null. */
  fulfillment: {
    status: string | null;
    provider: string | null;
    reference: string | null;
    carrier: string | null;
    trackingNumber: string | null;
    trackingUrl: string | null;
    labelUrl: string | null;
    deliveryMethod: string | null;
    shippingMethod: string | null;
    pickupAgent: string | null;
    selfArrangedInstruction: string | null;
    shippedAt: string | null;
    deliveredAt: string | null;
  };
  /** Status timeline, oldest first. Empty on the list read — only the by-id read returns it. */
  history: ApiOrderHistoryEntry[];
  customer: {
    name: string;
    email: string;
    phone: string;
  };

  items: {
    count: number;
    description: string;
    details: OrderItem[];
  };
  total: string;
  totalAmount: number;
  status: // | "PAID"
    | "PENDING"
    | "ORDER_CONFIRMED"
    | "ORDER_RECEIVED"
    | "TRANSFER_FAILED"
    | "REFUNDED"
    | "PROCESSING"
    | "EN_ROUTE"
    | "DELIVERED"
    | "CANCELLED"
    | "RETURNED";
  payment: "Pending" | "Failed" | "Refunded" | "Paid";
  category: "Agbada" | "Kaftan" | "Suits" | "Traditional" | "Accessories";
  location: string;
  shipping: {
    estimatedDelivery: string;
    address: {
      street: string;
      city: string;
      country: string;
      phone: string;
    };
  };
  paymentDetails: {
    method: string;
    totalAmount: number;
    processingFee: number;
    netAmount: number;
  };
  internalNotes: string;
}

// API Parameters
export interface OrdersParams {
  "page[size]"?: number;
  "page[after]"?: string;
  "page[before]"?: string;
  sort?: string[];
  "filter[status]"?:
    | "PAID"
    | "PENDING"
    | "ORDER_CONFIRMED"
    | "ORDER_RECEIVED"
    | "TRANSFER_FAILED"
    | "REFUNDED"
    | "PROCESSING"
    | "EN_ROUTE"
    | "DELIVERED"
    | "CANCELLED"
    | "RETURNED";
  "filter[user.email]"?: string;
  "filter[user.fullName]"?: string;
  "filter[items.itemName]"?: string;
  "filter[category]"?: string;
}

import { formatPrice } from '../../../shared/utils/priceUtils'

/** Pull one variant attribute out of the free-form `attributes` blob, case-insensitively. */
function attributeText(attributes: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  if (!attributes) return ''
  for (const [k, v] of Object.entries(attributes)) {
    if (keys.some((want) => k.toLowerCase() === want) && (typeof v === 'string' || typeof v === 'number')) {
      return String(v)
    }
  }
  return ''
}

/**
 * Who placed the order.
 *
 * The API sends no `customer` object — the buyer is `customerEmail`/`guestEmail` plus the names the
 * checkout stashed in `fulfillmentMetadata`. Reading the absent object is what rendered every row as
 * "Unknown Customer" / "No Email" even though the email was right there in the payload.
 */
function resolveCustomer(apiOrder: ApiOrder): Order['customer'] {
  const meta = apiOrder.fulfillmentMetadata
  const fullName = [meta?.firstName, meta?.lastName].filter(Boolean).join(' ').trim()
  const email = apiOrder.customer?.email || apiOrder.customerEmail || apiOrder.guestEmail || ''

  return {
    // Fall back to the email's local part before giving up — "lizzy22.mj" beats "Unknown Customer".
    name: apiOrder.customer?.fullName || fullName || (email ? email.split('@')[0] : '') || 'Guest',
    email: email || 'No Email',
    phone: apiOrder.contactPhone || meta?.contactPhone || apiOrder.address?.phone || 'N/A',
  }
}

// Transform function to convert API order to frontend order
export function transformApiOrderToOrder(apiOrder: ApiOrder): Order {
  // Transform items.
  //
  // ⚠️ Read FLAT first. The nested `productVariant.…` chain this used to dereference unconditionally
  // is absent from the live payload, so it threw a TypeError on every order that had items and the
  // whole Orders page failed to load. Every nested read is now optional-chained, and a missing name
  // degrades to the variant id rather than crashing or printing "undefined".
  const items: OrderItem[] = (apiOrder.items || []).map(item => {
    const legacy = item.productVariant?.productColour
    const product = legacy?.productMaterial?.productSize?.product
    const unitPrice = item.unitPrice ?? item.price ?? 0
    // `subtotal` is server-computed and already accounts for the discount — prefer it over
    // re-multiplying, which would disagree with the order total on a discounted line.
    const total = item.subtotal ?? unitPrice * item.quantity

    return {
      id: item.id,
      name: item.itemName || product?.name || (item.variantId ? `Variant ${item.variantId.slice(0, 8)}` : 'Item'),
      costPrice: product?.basePrice ?? item.originalPrice ?? 0,
      quantity: item.quantity,
      unitPrice,
      total,
      size: legacy?.productMaterial?.productSize?.size?.name ?? attributeText(item.attributes, 'size'),
      color: legacy?.colour?.name ?? attributeText(item.attributes, 'colour', 'color'),
      material: legacy?.productMaterial?.material?.name ?? attributeText(item.attributes, 'material'),
      variantId: item.variantId,
    }
  })

  // Map API status to frontend status
  const statusMap: Record<ApiOrder["status"], Order["status"]> = {
    // 'PAID': 'PROCESSING',
    PENDING: "PROCESSING",
    ORDER_CONFIRMED: "ORDER_CONFIRMED",
    ORDER_RECEIVED: "ORDER_RECEIVED",
    PROCESSING: "PROCESSING",
    EN_ROUTE: "EN_ROUTE",
    DELIVERED: "DELIVERED",
    TRANSFER_FAILED: "TRANSFER_FAILED",
    REFUNDED: "REFUNDED",
    CANCELLED: "CANCELLED",
    RETURNED: "RETURNED",
  };

  let totalAmount: number;
  if (apiOrder.total == null) {
    console.warn(`Warning: apiOrder.total is ${apiOrder.total} for order id ${apiOrder.id}. Defaulting to 0. This may indicate a data quality issue.`);
    totalAmount = 0;
  } else {
    totalAmount = apiOrder.total;
  }

  const isBespoke = apiOrder.orderType === 'BESPOKE'

  return {
    id: apiOrder.id,
    // The API sends `tenantId`; `merchantId` is the older name and is absent on development.
    merchantId: apiOrder.merchantId ?? apiOrder.tenantId ?? '',

    orderId: apiOrder.trackingId,
    friendlyOrderId: apiOrder.friendlyOrderId,
    date: apiOrder.createdAt,
    orderType: apiOrder.orderType,
    bespokeDetails: apiOrder.bespokeDetails,
    dhlTrackingNumber: apiOrder.dhlTrackingNumber,
    carrier: apiOrder.carrier,
    manualTrackingNumber: apiOrder.manualTrackingNumber,
    shippingMethod: apiOrder.shippingMethod,
    customer: resolveCustomer(apiOrder),
    fulfillment: {
      status: apiOrder.fulfillmentStatus ?? null,
      provider: apiOrder.fulfillmentProvider ?? null,
      reference: apiOrder.fulfillmentReference ?? null,
      carrier: apiOrder.carrier ?? null,
      // Three fields have carried the tracking number over time; show whichever is set.
      trackingNumber: apiOrder.trackingCode ?? apiOrder.manualTrackingNumber ?? apiOrder.dhlTrackingNumber ?? null,
      trackingUrl: apiOrder.trackingUrl ?? null,
      labelUrl: apiOrder.labelUrl ?? null,
      deliveryMethod: apiOrder.deliveryMethod ?? null,
      shippingMethod: apiOrder.shippingMethod ?? apiOrder.fulfillmentMetadata?.shippingMethod ?? null,
      pickupAgent: apiOrder.pickupAgent ?? null,
      selfArrangedInstruction: apiOrder.selfArrangedInstruction ?? null,
      shippedAt: apiOrder.shippedAt ?? null,
      deliveredAt: apiOrder.deliveredAt ?? null,
    },
    // Only the by-id read returns history; the list omits it, so default to empty.
    history: apiOrder.history ?? [],
    items: {
      count: isBespoke ? (apiOrder.bespokeDetails?.quantity ?? 0) : items.length,
      description: isBespoke ? (apiOrder.bespokeDetails?.garmentType ?? 'Bespoke Order') : items.map((item) => item.name).join(", "),
      details: items,
    },
    total: formatPrice(totalAmount),
    totalAmount: totalAmount,
    status: statusMap[apiOrder.status],
    payment: apiOrder.paymentReference ? "Paid" : "Pending", // Default - not provided by API
    category: "Traditional", // Default - would need to be determined from product data
    location:
      apiOrder.address?.city || apiOrder.address?.stateOrProvinceCode || "N/A",
    shipping: {
      estimatedDelivery: apiOrder.deliveryDate ?? "Not Specified", // Not provided by API
      address: {
        street: apiOrder.address?.streetLines?.[0] || "",
        city: apiOrder.address?.city || "",
        country: apiOrder.address?.countryCode || "",
        phone: apiOrder.address?.phone || "N/A",
      },
    },
    paymentDetails: {
      method: "N/A",
      totalAmount: totalAmount,
      processingFee: 0,
      netAmount: totalAmount,
    },
    internalNotes: apiOrder.additionalInfo,
  };
}